import {
  faFileAlt,
  faPencilAlt,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { B2Button } from "@hdb/biome-2";
import { FC, createRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getBlocksBySiteCode,
  setBlockOptionsData,
} from "src/store/slices/location-block.slice";
import * as yup from "yup";
import ModalComponent from "../../../../../core/components/modal/modal.component";
import TableComponent from "../../../../../core/components/table/table.component";
import HasPermission from "../../../../../core/guards/has-permission";
import SkeletonComponent from "../../../../../shared/custom-spinner/skeleton.component";
import FormBuilderComponent, {
  FormConfig,
} from "../../../../../shared/form-builder/form-builder.component";
import {
  addSite,
  deleteSite,
  editSite,
  getLocationSiteByCode,
  getLocationSites,
} from "../../../../../store/slices/location-site.slice";
import { getTownCouncils } from "../../../../../store/slices/town-council.slice";
import SearchBarComponent from "../../search-bar/search-bar.component";
import { getSiteProjectCategories } from "src/core/api/services/site.service";
import { useQuery } from "@tanstack/react-query";
import "../../../admin.scss";

interface SiteTableComponentProps {
  columns: any;
  data: any[];
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort: any;
  onRefetchData: () => void;
  isLoading: boolean;
  onSearch: (val: string) => void;
}

const SiteTableComponent: FC<SiteTableComponentProps> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  onRefetchData,
  isLoading,
  onSearch,
}) => {
  const formRef = createRef();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDetails, setIsDetails] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [blockOptions, setBlockOptions] = useState<any>([]);

  const [selectedCodeToDeleteOrEdit, setSelectedCodeToDeleteOrEdit] =
    useState<number>(0);
  const [townCouncilOptions, setTownCouncilOptions] = useState<any>([]);
  const [projectCategoryOptions, setProjectCategoryOptions] = useState<any>([]);
  const [initialValueSetCount, setInitialValueSetCount] = useState<any>(0);

  const [initialValues, setInitialValues] = useState<any>({
    name: "",
    siteCode: "",
    tcCode: "",
    siteProjectTypeId: "",
    defaultBlockCode: null,
    shortDescription: "",
  });

  const dispatch = useDispatch();

  const locationSiteDetails = useSelector((state: any) => {
    return state.locationSite.locationSiteDetails;
  });

  const isLoadingLocationSiteDetails = useSelector((state: any) => {
    return state.locationSite.isLoadingLocationSiteDetails;
  });

  const townCouncilsResponse = useSelector((state: any) => {
    return state.townCouncil.townCouncils;
  });

  const blockDataSiteCode = useSelector((state: any) => {
    return state.locationBlock.blocksBySiteCode;
  });

  let { data: projectCategoryResponse } = useQuery({
    queryKey: ["getSiteProjectCategories"],
    queryFn: ({ signal }) => getSiteProjectCategories(signal),
  });

  useEffect(() => {
    const townCouncils = townCouncilsResponse?.data?.map((tc: any) => ({
      label: tc?.name,
      value: tc?.tcCode,
    }));

    setTownCouncilOptions(townCouncils);
  }, [townCouncilsResponse]);

  useEffect(() => {
    const projectCategories = projectCategoryResponse?.data?.map((pc: any) => ({
      label: pc?.name,
      value: pc?.id,
    }));

    setProjectCategoryOptions(projectCategories);
  }, [projectCategoryResponse]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsDetails(false);
    setIsEdit(false);
    setIsDelete(false);

    setInitialValues({
      name: "",
      siteCode: "",
      tcCode: "",
      siteProjectTypeId: "",
      defaultBlockCode: null,
      shortDescription: "",
    });
  };

  const detailsAction = (siteCode: any) => {
    dispatch(getLocationSiteByCode({ siteCode }));

    setIsDetails(true);
    setIsModalOpen(true);
    setIsEdit(false);
    setIsDelete(false);
  };

  const editAction = (siteCode: any) => {
    dispatch(getLocationSiteByCode({ siteCode }));

    setSelectedCodeToDeleteOrEdit(siteCode);
    setIsDetails(false);
    setIsModalOpen(true);
    setIsEdit(true);
    setIsDelete(false);
  };

  const deleteAction = (siteCode: any) => {
    setIsDetails(false);
    setIsModalOpen(true);
    setIsEdit(false);
    setIsDelete(true);

    setSelectedCodeToDeleteOrEdit(siteCode);
  };

  const handleDelete = () => {
    dispatch(deleteSite({ siteCode: selectedCodeToDeleteOrEdit })).then(() => {
      onRefetchData();
      handleCloseModal();
    });
  };

  const handleAddSite = () => {
    setIsCreate(true);
    setIsDetails(false);
    setIsModalOpen(true);
    setIsEdit(false);
    setIsDelete(false);
  };

  useEffect(() => {
    dispatch(setBlockOptionsData([]));
    dispatch(getTownCouncils({ PageNumber: 0, pageSize: 0 }));
  }, []);

  useEffect(() => {
    if (!locationSiteDetails || !Object.keys(locationSiteDetails).length)
      return;

    dispatch(getBlocksBySiteCode({ siteCode: locationSiteDetails?.siteCode }));

    setInitialValues({
      name: locationSiteDetails?.name || "",
      siteCode: locationSiteDetails?.siteCode || "",
      tcCode: locationSiteDetails?.tcCode || "",
      siteProjectTypeId: locationSiteDetails?.siteProjectTypeId || "",
      defaultBlockCode: locationSiteDetails?.defaultBlockCode || null,
      shortDescription: locationSiteDetails?.shortDescription || "",
    });
  }, [locationSiteDetails]);

  useEffect(() => {
    const blocks = blockDataSiteCode?.data?.map((block: any) => ({
      label: block?.blockName,
      value: block?.blockCode,
    }));

    setBlockOptions(blocks);
  }, [blockDataSiteCode]);

  useEffect(() => {
    // console.log(initialValueSetCount);
    setInitialValueSetCount(initialValueSetCount + 1);
  }, [initialValues]);

  const onSubmit = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();
      const { name, siteCode, tcCode, siteProjectTypeId, shortDescription } =
        form.values;

      if (
        !name ||
        !siteCode ||
        !tcCode ||
        !siteProjectTypeId ||
        siteCode?.length < 2 ||
        siteCode?.length > 20 ||
        name?.length < 2 ||
        name?.length > 50 ||
        shortDescription?.length > 100
      ) {
        return;
      }
      // debugger;

      if (isEdit) {
        const formValues = form.values;
        const payload = {
          ...formValues,
          defaultBlockCode:
            formValues.defaultBlockCode === ""
              ? null
              : formValues.defaultBlockCode,
          siteCode: selectedCodeToDeleteOrEdit,
        };

        dispatch(
          editSite({ payload, siteCode: selectedCodeToDeleteOrEdit })
        ).then(() => {
          handleCloseModal();
          onRefetchData();
        });
      } else {
        dispatch(addSite({ payload: form.values })).then(() => {
          handleCloseModal();
          onRefetchData();
        });
      }
    }
  };

  let modalActionConfig: any = [];

  if (isDetails) {
    modalActionConfig = [];
  } else if (isDelete) {
    modalActionConfig = [
      {
        label: "Yes",
        event: handleDelete,
        variant: "primary",
      },
    ];
  } else {
    modalActionConfig = [
      {
        label: "Save",
        event: onSubmit,
        variant: "primary",
      },
    ];
  }

  const actionConfig: any = [
    {
      label: "Edit",
      actionProperty: "siteCode",
      event: editAction,
      variant: "secondary",
      className: "mx-2 my-1",
      moduleName: "Site",
      actionName: "Edit-Site",
      icon: faPencilAlt,
    },
    {
      label: "View",
      actionProperty: "siteCode",
      event: detailsAction,
      variant: "info",
      className: "mx-2 my-1",
      moduleName: "Site",
      actionName: "View-Site",
      icon: faFileAlt,
    },
    {
      label: "Delete",
      actionProperty: "siteCode",
      event: deleteAction,
      variant: "danger",
      className: "mx-2 my-1",
      moduleName: "Site",
      actionName: "Delete-Site",
      icon: faTrashAlt,
    },
  ];

  const emptyConfig = {
    title: "No Site Found",
    description: "",
  };

  const formConfig: FormConfig = {
    controls: [
      {
        name: "siteCode",
        label: "Site Code",
        placeholder: "AMK018",
        type: "text",
        required: !isDetails,
        readOnly: isDetails || isEdit,
        disabled: isDetails || isEdit,
      },
      {
        name: "name",
        label: "Site Name",
        placeholder: "Bishan",
        type: "text",
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
      // {
      //   name: "defaultBlockCode",
      //   label: "Default Block",
      //   placeholder: "Select Default Block",
      //   type: "select",
      //   options: blockOptions,
      //   required: false,
      //   readOnly: isDetails,
      //   disabled: isDetails || (!isEdit && isCreate),
      // },
      // Conditionally include defaultBlockCode
      ...(isEdit || isDetails
        ? [
          {
            name: "defaultBlockCode",
            label: "Default Block",
            placeholder: "Select Default Block",
            type: "searchable-select",
            options: blockOptions,
            required: false,
            readOnly: isDetails,
            disabled: isDetails || (!isEdit && isCreate),
          },
        ]
        : []),
      {
        name: "tcCode",
        label: "Town Council",
        placeholder: "Select Town Council",
        type: "searchable-select",
        options: townCouncilOptions,
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
      {
        name: "siteProjectTypeId",
        label: "Project Category",
        placeholder: "Select Project Category",
        type: "select",
        options: projectCategoryOptions,
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
      {
        name: "shortDescription",
        label: "Short Description",
        placeholder: "Something...",
        type: "textarea",
        required: false,
        readOnly: isDetails,
        disabled: isDetails,
      },
    ],
  };

  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .min(2, "Site Name must be at least 2 characters")
      .max(50, "Site Name should not exceed 50 characters")
      .required("Site Name is required"),
    siteCode: yup
      .string()
      .min(2, "Site Code must be at least 2 characters")
      .max(20, "Site Code should not exceed 20 characters")
      .required("Site Code is required"),
    tcCode: yup.string().required("Town Council is required"),
    siteProjectTypeId: yup.string().required("Project Category is required"),
    shortDescription: yup
      .string()
      .max(100, "Short Description should not exceed 100 characters"),
  });

  return (
    <div>
      <div>
        <div className="d-flex gap-3 justify-content-end table-generic-action table-toolbar">
          <SearchBarComponent
            placeholder="Search Site"
            methodToDispatch={getLocationSites}
            getSearch={onSearch}
          />
          <HasPermission
            moduleName="Site"
            actionName="Add-Site"
            isRoute={false}
          >
            <B2Button
              className="icon-text-btn"
              variant="primary"
              onClick={handleAddSite}
            >
              {/* <FontAwesomeIcon icon={faCirclePlus} className="fs-7 me-2" /> */}
              Add New Site
            </B2Button>
          </HasPermission>
        </div>
        <div className="my-3">
          <TableComponent
            tableHeaders={columns}
            emptyConfig={emptyConfig}
            data={data}
            totalNumberOfRecords={totalNumberOfRecords}
            pageSize={pageSize}
            current={current}
            onChangePage={onChangePage}
            actionConfig={actionConfig}
            onChangeSort={onChangeSort}
            isLoadingData={isLoading}
          />
        </div>
      </div>

      {/* modals */}

      {isModalOpen && (
        <ModalComponent
          isConformationModal={isDelete}
          showModal={isModalOpen}
          handleClose={handleCloseModal}
          headerTitle={
            isDetails
              ? "Site Details"
              : isEdit
                ? "Edit Site"
                : isDelete
                  ? "Delete Site"
                  : "Add New Site"
          }
          actionConfig={modalActionConfig}
        >
          {isDelete ? (
            <h5 className="text-center">
              Are you sure to <b className="text-danger">Delete</b>?
            </h5>
          ) : (
            <SkeletonComponent
              wrapperHeight="280px"
              skeletonHeight="50px"
              isLoading={isLoadingLocationSiteDetails}
              count={4}
            >
              <FormBuilderComponent
                // renderCount={initialValueSetCount}
                formRef={formRef}
                initialValues={initialValues}
                validation={validationSchema}
                formConfig={formConfig}
              />
            </SkeletonComponent>
          )}
        </ModalComponent>
      )}
    </div>
  );
};

export default SiteTableComponent;
